import {
  type CrossroadsLog,
  useLatestSagaEvent,
  useOrderCheckpointsMetadata,
} from '@/api/hooks/useIntegrationLogs'
import { friendlySagaName } from '@/constants/sagaTypes'
import type { components } from '@/types/api/v1'
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  MinusCircleOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Popover, message } from 'antd'
import { useMemo, useState } from 'react'
import {
  CORE_ORDER_CHECKPOINTS,
  OPTIONAL_ORDER_CHECKPOINTS,
} from './orderCheckpoints'

type IntegrationCheckpoints = components['schemas']['IntegrationCheckpoints']
type CheckpointStatus = components['schemas']['CheckpointStatus']
type SagaType = components['schemas']['SagaType']

/** Opens a checkpoint's latest event in the details sidebar. Handed down from
 * the grid that owns the sidebar (see LogsGrid), so a column rendered without
 * one just leaves the popover rows inert. */
export type CheckpointLogSelect = (log: CrossroadsLog) => void

const DIVIDER_COLOR = '#999'

const DEFAULT_STATUS_COLOR = 'var(--gray-400)'

const SILENCED_OPACITY = 'var(--silenced-opacity)'

const STATUS_COLORS: Record<CheckpointStatus, string> = {
  succeeded: 'var(--theme-success)',
  partial_success: 'var(--theme-warning)',
  warning: 'var(--theme-warning)',
  failed: 'var(--theme-error)',
  pending: DEFAULT_STATUS_COLOR,
  overwritten: DEFAULT_STATUS_COLOR,
}

function dotColor(status: CheckpointStatus | null | undefined): string {
  return status
    ? (STATUS_COLORS[status] ?? DEFAULT_STATUS_COLOR)
    : DEFAULT_STATUS_COLOR
}

const PARTIAL_SUCCESS_GRADIENT = `linear-gradient(135deg, ${STATUS_COLORS.succeeded} 50%, ${STATUS_COLORS.failed} 50%)`

function PartialSuccessDot({
  size = 10,
  opacity,
}: { size?: number; opacity?: React.CSSProperties['opacity'] }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: PARTIAL_SUCCESS_GRADIENT,
        flexShrink: 0,
        opacity,
      }}
    />
  )
}

function Dot({
  status,
  silenced,
}: { status: CheckpointStatus | null | undefined; silenced?: boolean }) {
  const opacity = silenced ? SILENCED_OPACITY : undefined
  if (status === 'partial_success')
    return <PartialSuccessDot opacity={opacity} />
  return <div style={{ ...dotStyle(dotColor(status)), opacity }} />
}

function StatusIcon({
  status,
}: { status: CheckpointStatus | null | undefined }) {
  if (status === 'succeeded')
    return <CheckCircleFilled style={{ color: STATUS_COLORS.succeeded }} />
  if (status === 'partial_success') return <PartialSuccessDot size={14} />
  if (status === 'warning')
    return <WarningFilled style={{ color: STATUS_COLORS.warning }} />
  if (status === 'failed')
    return <CloseCircleFilled style={{ color: STATUS_COLORS.failed }} />
  return <MinusCircleOutlined style={{ color: DEFAULT_STATUS_COLOR }} />
}

type CheckpointEntry = {
  key: string
  label: string
  status: CheckpointStatus | null | undefined
  silenced: boolean
}

function toEntries(
  checkpoints: IntegrationCheckpoints,
  defs: [string, string][],
  silencedKeys?: Set<string>,
): CheckpointEntry[] {
  return defs.map(([key, label]) => ({
    key,
    label,
    status: checkpoints[key as keyof typeof checkpoints] as
      | CheckpointStatus
      | null
      | undefined,
    silenced: silencedKeys?.has(key) ?? false,
  }))
}

function CheckpointRow({
  entry,
  saga,
  loading,
  onSelect,
}: {
  entry: CheckpointEntry
  /** The saga behind this checkpoint, from the server's silencing metadata.
   * Absent while that metadata is still loading, which is the one case an
   * order checkpoint has nothing to fetch. */
  saga?: SagaType
  loading?: boolean
  onSelect?: (saga: SagaType, checkpointKey: string) => void
}) {
  const clickable = !!saga && !!onSelect
  return (
    <Horizontal
      gap={8}
      alignItems='center'
      style={entry.silenced ? { opacity: SILENCED_OPACITY } : undefined}
      className={clickable ? 'cursor-pointer' : undefined}
      onClick={clickable ? () => onSelect(saga, entry.key) : undefined}
    >
      {loading ? <LoadingOutlined /> : <StatusIcon status={entry.status} />}
      <Texto>{entry.label}</Texto>
    </Horizontal>
  )
}

const SUPPLY_PLAN_CORE_CHECKPOINTS: [string, string][] = [
  ['mapped_status', 'Mapped'],
  ['supply_upserted_status', 'Supply Synced'],
]

// Pricing isn't part of every supply plan batch — kept on the "other" side
// of the divider, same as Orders' optional lifecycle checkpoints.
const SUPPLY_PLAN_OTHER_CHECKPOINTS: [string, string][] = [
  ['price_upserts_status', 'Price Synced'],
]

type SupplyPlanBatchCheckpoints = Extract<
  IntegrationCheckpoints,
  { type: 'SupplyPlanBatchCheckpoints' }
>

// Denominator is null when the checkpoint doesn't apply yet (e.g. pricing
// hasn't been attempted) — shown as "-" rather than a misleading "0 / 0".
function supplyPlanCheckpointCount(
  checkpoints: SupplyPlanBatchCheckpoints,
  key: string,
): string {
  switch (key) {
    case 'mapped_status':
      if (checkpoints.supply_plan_count == null) return '-'
      return `${checkpoints.mapped_count ?? 0} / ${checkpoints.supply_plan_count}`
    case 'price_upserts_status':
      if (checkpoints.price_upserts_attempted == null) return '-'
      return `${checkpoints.price_upserts_succeeded ?? 0} / ${checkpoints.price_upserts_attempted}`
    case 'supply_upserted_status': {
      if (checkpoints.mapped_count == null) return '-'
      // Plans bound for a non-S&D destination are never upserted to S&D, but they
      // were still delivered — they count toward the numerator via skipped.
      const delivered =
        (checkpoints.supply_upserted_count ?? 0) +
        (checkpoints.supply_upsert_skipped ?? 0)
      return `${delivered} / ${checkpoints.mapped_count}`
    }
    default:
      return '-'
  }
}

function SupplyPlanCheckpointRow({
  entry,
  count,
}: { entry: CheckpointEntry; count: string }) {
  return (
    <Horizontal gap={16} alignItems='center' justifyContent='space-between'>
      <Horizontal gap={8} alignItems='center'>
        <StatusIcon status={entry.status} />
        <Texto>{entry.label}</Texto>
      </Horizontal>
      <Texto appearance='hint'>{count}</Texto>
    </Horizontal>
  )
}

function SupplyPlanBatchCheckpointsDots({
  checkpoints,
}: { checkpoints: SupplyPlanBatchCheckpoints }) {
  const coreEntries = toEntries(checkpoints, SUPPLY_PLAN_CORE_CHECKPOINTS)
  const otherEntries = toEntries(checkpoints, SUPPLY_PLAN_OTHER_CHECKPOINTS)

  return (
    <Popover
      content={
        <Vertical gap={4}>
          {coreEntries.map((e) => (
            <SupplyPlanCheckpointRow
              key={e.key}
              entry={e}
              count={supplyPlanCheckpointCount(checkpoints, e.key)}
            />
          ))}
          <div
            style={{ borderTop: `1px solid ${DIVIDER_COLOR}`, margin: '4px 0' }}
          />
          {otherEntries.map((e) => (
            <SupplyPlanCheckpointRow
              key={e.key}
              entry={e}
              count={supplyPlanCheckpointCount(checkpoints, e.key)}
            />
          ))}
          {checkpoints.overwritten != null &&
            checkpoints.supply_plan_count != null && (
              // Not a checkpoint/status -- no dot, just a plain count of how
              // many of this batch's supply plans have since been reclaimed
              // by a later batch (see SupplyPlanBatchCheckpoints.overwritten,
              // shared/models/logs.py).
              <Texto appearance='hint' style={{ textAlign: 'center' }}>
                ({checkpoints.overwritten}/{checkpoints.supply_plan_count}{' '}
                overwritten)
              </Texto>
            )}
        </Vertical>
      }
      trigger='hover'
      getPopupContainer={() => document.body}
      overlayStyle={{ maxWidth: 220 }}
    >
      <div style={containerStyle}>
        {coreEntries.map((e) => (
          <Dot key={e.key} status={e.status} />
        ))}
        <div
          style={{
            alignSelf: 'stretch',
            borderLeft: `1px solid ${DIVIDER_COLOR}`,
            margin: '2px 3px',
          }}
        />
        {otherEntries.map((e) => (
          <Dot key={e.key} status={e.status} />
        ))}
      </div>
    </Popover>
  )
}

type OrderCheckpoints = Extract<
  IntegrationCheckpoints,
  { type: 'OrderIntegrationCheckpoints' }
>

function OrderCheckpointsDots({
  checkpoints,
  logGroupId,
  onLogSelected,
}: {
  checkpoints: OrderCheckpoints
  logGroupId?: string
  onLogSelected?: CheckpointLogSelect
}) {
  // Silencing is stored per saga, and a saga can drive more than one
  // checkpoint (`completed` and `status_updates` are both progress_status),
  // so the server's checkpoint -> saga map is what turns it back into a set
  // of dulled dots. Cached indefinitely, so this is one fetch for the grid.
  const { data: metadata } = useOrderCheckpointsMetadata()

  // The same map is what turns a clicked row into the saga whose latest event
  // the sidebar should show. Tracked by checkpoint key rather than by saga so
  // that the two progress_status rows spin independently.
  const { mutate: fetchLatestSagaEvent } = useLatestSagaEvent()
  const [pendingKey, setPendingKey] = useState<string | null>(null)

  const selectSagaLog =
    logGroupId && onLogSelected
      ? (saga: SagaType, checkpointKey: string) => {
          setPendingKey(checkpointKey)
          fetchLatestSagaEvent(
            { params: { query: { log_group_id: logGroupId, saga } } },
            {
              onSuccess: (log) => {
                // A checkpoint can be drawn (pending, or reached indirectly)
                // without the saga behind it having ever logged on this order.
                if (log) onLogSelected(log)
                else
                  message.info(
                    `No ${friendlySagaName(saga) ?? saga} events on this order yet`,
                  )
              },
              onSettled: () => setPendingKey(null),
            },
          )
        }
      : undefined

  const silencedKeys = useMemo(() => {
    const silenced = new Set<string>(checkpoints.silenced_sagas)
    return new Set(
      Object.values(metadata ?? {})
        .filter((entry) => silenced.has(entry.saga))
        .map((entry) => entry.checkpoint_key),
    )
  }, [metadata, checkpoints.silenced_sagas])

  const coreEntries = toEntries(
    checkpoints,
    CORE_ORDER_CHECKPOINTS,
    silencedKeys,
  )
  const otherEntries = toEntries(
    checkpoints,
    OPTIONAL_ORDER_CHECKPOINTS,
    silencedKeys,
  )

  const renderRow = (e: CheckpointEntry) => (
    <CheckpointRow
      key={e.key}
      entry={e}
      saga={metadata?.[e.key]?.saga}
      loading={pendingKey === e.key}
      onSelect={selectSagaLog}
    />
  )

  return (
    <Popover
      content={
        <Vertical gap={4}>
          {coreEntries.map(renderRow)}
          <div
            style={{ borderTop: `1px solid ${DIVIDER_COLOR}`, margin: '4px 0' }}
          />
          {otherEntries.map(renderRow)}
        </Vertical>
      }
      trigger='hover'
      getPopupContainer={() => document.body}
      overlayStyle={{ maxWidth: 180 }}
    >
      <div style={containerStyle}>
        {coreEntries.map((e) => (
          <Dot key={e.key} status={e.status} silenced={e.silenced} />
        ))}
        <div
          style={{
            alignSelf: 'stretch',
            borderLeft: `1px solid ${DIVIDER_COLOR}`,
            margin: '2px 3px',
          }}
        />
        {otherEntries.map((e) => (
          <Dot key={e.key} status={e.status} silenced={e.silenced} />
        ))}
      </div>
    </Popover>
  )
}

export function CheckpointsDots({
  checkpoints,
  logGroupId,
  onLogSelected,
}: {
  checkpoints: IntegrationCheckpoints
  /** The row's log group, i.e. what a clicked checkpoint fetches its latest
   * event from. Order checkpoints only. */
  logGroupId?: string
  onLogSelected?: CheckpointLogSelect
}) {
  if (checkpoints.type === 'SupplyPlanBatchCheckpoints') {
    return <SupplyPlanBatchCheckpointsDots checkpoints={checkpoints} />
  }

  if (checkpoints.type === 'AlwaysFailCheckpoints') {
    return (
      <Popover
        content={
          <Horizontal gap={8} alignItems='center'>
            <CloseCircleFilled style={{ color: STATUS_COLORS.failed }} />
            <Texto>Integration Failed</Texto>
          </Horizontal>
        }
        trigger='hover'
        getPopupContainer={() => document.body}
        overlayStyle={{ maxWidth: 180 }}
      >
        <div style={containerStyle}>
          <div style={dotStyle('var(--theme-error)')} />
        </div>
      </Popover>
    )
  }

  return (
    <OrderCheckpointsDots
      checkpoints={checkpoints}
      logGroupId={logGroupId}
      onLogSelected={onLogSelected}
    />
  )
}

const containerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  width: '100%',
  height: '100%',
  cursor: 'default',
}

function dotStyle(color: string): React.CSSProperties {
  return {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: color,
    flexShrink: 0,
  }
}
